import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpEvent } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ApiService {

  // Base URL for merchant authentication APIs
  private API_URL = `${environment.apiUrl}/api/merchant`;

  constructor(private http: HttpClient) {}


  //
  private resetHeaders(resetToken: string): HttpHeaders {
  return new HttpHeaders({
    'Content-Type': 'application/json',
    subscriberid: environment.subscriberId,
    created_by: environment.subscriberId,
    Authorization: `${resetToken}`, //  RESET TOKEN
  });
}

  // Build request headers with subscriberId and optional token
  private authHeaders(includeAuth: boolean = true): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      subscriberid: environment.subscriberId,
      // created_by: environment.subscriberId,
    });

    if (includeAuth) {
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }


  googleLogin(payload: { token: string }) {
    return this.http.post<any>(
      `${this.API_URL}/google/login`,
      payload,
      { headers: this.authHeaders(false) }
    );
  }


  // Verify OTP for Password Reset
verifyPasswordOtp(payload: { email: string; otp: string;}): Observable<any> {
  return this.http.post(
    `${this.API_URL}/password/verify-otp`,
    payload,
    { headers: this.authHeaders(false) } // No auth token
  );
}

// Verify OTP for Email Verification (post-registration)
verifyEmailOtp(payload: { email: string; otp: string }): Observable<any> {
  return this.http.post(
    `${this.API_URL}/v1/auth/verify-email`,
    payload,
    { headers: this.authHeaders(false) }
  );
}

// Resend Email Verification OTP
resendVerifyOtp(email: string): Observable<any> {
  return this.http.post(
    `${this.API_URL}/v1/auth/resend-verify`,
    { email },
    { headers: this.authHeaders(false) }
  );
}

// Update / Reset Password
updatePassword(
  payload: {
    email: string;
    password: string;
    confirmPassword: string;
  },
  resetToken: string
): Observable<any> {
  return this.http.post(
    `${this.API_URL}/password/update`,
    payload,
    { headers: this.resetHeaders(resetToken) }
  );
}



  // Request OTP for Password Reset
  requestPasswordOtp(email: string): Observable<any> {
    return this.http.post(
      `${this.API_URL}/password/request-otp`,
      { email },
      { headers: this.authHeaders(false) } // No auth token needed
    );
  }



  // Login merchant user
  login(payload: { email: string; password: string }): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/auth/login`, // POST /api/merchant/v1/auth/login
      payload,
      { headers: this.authHeaders(false) } // No token required
    );
  }

  loadClientConfig(){
    return this.http.get(
      `${this.API_URL}/v1/auth/loadConfig`,
      {headers:this.authHeaders(true)}
    )
  }
  // Register merchant user
  register(payload: FormData): Observable<any> {
    const headers = this.authHeaders(false).delete('Content-Type');
    return this.http.post(
      `${this.API_URL}/v1/auth/register`,
      payload,
      { headers }
    );
  }

  sendInvite(payload: { email: string; roleId: string; storeId?: string }): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/invite`,
      payload,
      { headers: this.authHeaders(true) }
    );
  }

  getPendingInvites(): Observable<any> {
    return this.http.get(
      `${this.API_URL}/v1/invite`,
      { headers: this.authHeaders(true) }
    );
  }

  revokeInvite(inviteId: string): Observable<any> {
    return this.http.delete(
      `${this.API_URL}/v1/invite/${inviteId}`,
      { headers: this.authHeaders(true) }
    );
  }

  previewInvite(token: string): Observable<any> {
    return this.http.get(
      `${this.API_URL}/v1/invite/${token}`,
      { headers: this.authHeaders(false) }
    );
  }

  acceptInvite(token: string, payload: { name: string; mobile: string; password: string }): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/invite/${token}/accept`,
      payload,
      { headers: this.authHeaders(false) }
    );
  }

  // Enable / Disable MFA for logged-in user
updateMfaStatus(mfaEnabled: boolean): Observable<any> {
  return this.http.patch(
    `${this.API_URL}/user/me/mfa`,
    { mfaEnabled },
    { headers: this.authHeaders(true) }
  );
}


/**
 * Check if user exists by email POST /user/check-email
 */
checkUserByEmail(
  email: string
): Observable<{ success: boolean; exists: boolean; mfaEnabled: boolean }> {
  return this.http.post<{ success: boolean; exists: boolean; mfaEnabled: boolean }>(
    `${this.API_URL}/user/check-email`,
    { email },
    { headers: this.authHeaders(false) } // no auth token needed
  );
}


  // Get logged-in user name + organization logo
  getUserAndLogo(): Observable<any> {
    return this.http.get(
      `${this.API_URL}/user/me/basic`,
      { headers: this.authHeaders(true) }
    );
  }

  // Get logged-in user with full organization details
    getMeWithOrganizationDetails(): Observable<any> {
      return this.http.get(
        `${this.API_URL}/user/me/details`,
        { headers: this.authHeaders(true) }
      );
    }


getActiveOrders(filters: Record<string, any> = {}): Observable<any> {
  let headers = this.authHeaders(true);
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      headers = headers.set(key, String(value));
    }
  });
  return this.http.get(
    `${this.API_URL}/v1/orders/active`,
    { headers }
  );
}


  // Logout merchant user
  logout(): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/auth/logout`, 
      {},
      { headers: this.authHeaders(true) } // Token required
    );
  }



  // Alternative create endpoint (if backend still uses /stores/create)
  createStoreAlt(payload: any): Observable<any> {
    return this.http.post(
      `${this.API_URL}/stores/create`,
      payload,
      { headers: this.authHeaders(true) }
    );
  }

  // Get Users By Store ID
getUsersByStore(storeId: string): Observable<any> {
  return this.http.get(
    `${this.API_URL}/store/${storeId}/users`,
    { headers: this.authHeaders(true) }
  );
}

  // Update Store (PUT /stores/:storeId)
  updateStore(storeId: string, payload: any): Observable<any> {
    return this.http.put(
      `${this.API_URL}/stores/${storeId}`,
      payload,
      { headers: this.authHeaders(true) }
    );
  }

  // Delete Store (DELETE /stores/:storeId)
  deleteStore(storeId: string): Observable<any> {
    return this.http.delete(
      `${this.API_URL}/stores/${storeId}`,
      { headers: this.authHeaders(true) }
    );
  }

  // Get Store By ID (GET /api/merchant/stores/:storeId)
  getStoreById(storeId: string): Observable<any> {
    return this.http.get(
      `${this.API_URL}/stores/${storeId}`,
      { headers: this.authHeaders(true) }
    );
  }


  // Get All Stores (GET /stores)
  getAllStores(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Observable<any> {
    return this.http.get(
      `${this.API_URL}/stores`,
      {
        headers: this.authHeaders(true),
        params: params ? this.buildParams(params) : undefined,
      }
    );
  }

  getActiveStores(params:any){
    return this.http.get(
      `${this.API_URL}/stores/active`,
      {
        headers: this.authHeaders(true),
        params: params ? this.buildParams(params) : undefined,
      }
    )
  }
  // Get Orders
  getOrders(): Observable<any> {
  return this.http.get(
    `${this.API_URL}/v1/orders`,
    { headers: this.authHeaders(true) }
  );
}


  // Get Stores By Organization
  getStoresByOrganization(organizationId: string): Observable<any> {
    return this.http.get(
      `${this.API_URL}/v1/organizations/${organizationId}/stores`,
      { headers: this.authHeaders(true) }
    );
  }


  // Get all roles (Super Admin only)
  getRolesMe(): Observable<any> {
    return this.http.get(
      `${this.API_URL}/roles/me`,
      { headers: this.authHeaders(true) }
    );
  }


  // Get Stores mapped to logged-in user
getStoresByUser(): Observable<any> {
  return this.http.get(
    `${this.API_URL}/stores/by-user`,
    { headers: this.authHeaders(true) }
  );
}


// Toggle Store Active / Inactive
toggleStoreStatus(storeId: string): Observable<any> {
  return this.http.patch(
    `${this.API_URL}/stores/${storeId}/toggle`,
    {},
    { headers: this.authHeaders(true) }
  );
}

// Request MFA OTP (requires auth)
requestMfaOtp(): Observable<any> {
  return this.http.post(
    `${this.API_URL}/user/me/mfa/request-otp`,
    {}, // no body needed
    { headers: this.authHeaders(true) } // auth required
  );
}


// Verify MFA OTP (requires auth)
verifyMfaOtp(otp: string): Observable<any> {
  return this.http.post(
    `${this.API_URL}/user/me/mfa/verify-otp`,
    { otp },
    { headers: this.authHeaders(true) } // auth required
  );
}

  // Get All Organizations (GET /organizations)
  getAllOrganizations(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Observable<any> {
    return this.http.get(
      `${this.API_URL}/organizations`,
      {
        headers: this.authHeaders(true),
        params: params ? this.buildParams(params) : undefined,
      }
    );
  }




  // GET /organization/:id
  getOrganizationById(): Observable<any> {
    return this.http.get(
      `${this.API_URL}/organization/details`,
      { headers: this.authHeaders(true) }
    );
  }

  // POST /organization/:id
updateOrganization(organizationId: string, payload: any): Observable<any> {
  const isFormData = payload instanceof FormData;

  const headers = isFormData
    ? this.authHeaders(true).delete('Content-Type')
    : this.authHeaders(true);

  return this.http.post(
    `${this.API_URL}/organization/${organizationId}`,
    payload,
    { headers }
  );
}

verifyOrganization(organizationId: string): Observable<any> {
  return this.http.post(
    `${this.API_URL}/organization/${organizationId}/verify`,
    {},
    { headers: this.authHeaders(true) }
  );
}


/**
 * Get Orders By Manifest Status
 * MANIFEST | UNMANIFEST
 */
getOrdersByManifestStatus(options: {
  manifestStatus: 'MANIFEST' | 'UNMANIFEST';
  page?: number;
  limit?: number;
  date:object;
  status:string;
  order_id:string;
  customer:string;
  phone:string;
}): Observable<any> {
  let headers = this.authHeaders(true)

    // .set('x-manifest-status', options.manifestStatus);
  console.log((options))
  if(options.manifestStatus){
    headers = headers.set('x-manifest-status',String(options.manifestStatus))
  }
  if (options.page) {
    headers = headers.set('x-page', String(options.page));
  }
  headers = headers.set('x-date',JSON.stringify(options.date))
  if (options.limit) {
    headers = headers.set('x-limit', String(options.limit));
  }
  if(options.customer){
  headers = headers.set('x-customer',String(options.customer))
  }
  else if(options.phone){
  headers = headers.set('x-phone',String(options.phone))
  }
  else if(options.order_id){
  headers = headers.set('x-order',String(options.order_id))
  }
  else if(options.status){
    headers = headers.set('x-status',String(options.status))
  }
  return this.http.get(
    `${this.API_URL}/v1/orders/manifest-status`,
    { headers }
  );
}



  // Update Order Status (READY / CANCEL)
updateOrderAction(
  orderId: string,
  payload: { action: 'MARK_READY' | 'CANCEL' }
) {
  return this.http.patch(
    `${this.API_URL}/v1/orders/${orderId}/action`,
    payload,
    { headers: this.authHeaders(true) }
  );
}


getStoreSettingsByOrderId(
  orderId: string,
  options?: {
    storeIdentifier?: string;
    organizationName?: string;
    page?: number;
    limit?: number;
  }
): Observable<any> {
  const params = options ? this.buildParams(options) : undefined;

  return this.http.get(
    `${this.API_URL}/v1/orders/${orderId}/store-settings`,
    {
      headers: this.authHeaders(true),
      params,
    }
  );
}

getOrderStatusTimeline(orderId: string): Observable<any> {
  return this.http.get(
    `${this.API_URL}/v1/orders/${orderId}/status-timeline`,
    { headers: this.authHeaders(true) }
  );
}


// Order Report (Delivered / RTO / Cancelled / Completed)
getOrderReport(options?: {
  status?: string;
  search?: string;
  startDate?: Date;
  endDate?: Date;
  page?: number;
  limit?: number;
}): Observable<any> {

  let headers = this.authHeaders(true);

  if (options?.status) {
    headers = headers.set('x-status', options.status);
  }

  if (options?.search) {
    headers = headers.set('x-search', options.search);
  }

  if (options?.startDate) {
    headers = headers.set(
      'x-start-date',
      options.startDate.toISOString()
    );
  }

  if (options?.endDate) {
    headers = headers.set(
      'x-end-date',
      options.endDate.toISOString()
    );
  }

  if (options?.page) {
    headers = headers.set('x-page', String(options.page));
  }

  if (options?.limit) {
    headers = headers.set('x-limit', String(options.limit));
  }

  return this.http.get(
    `${this.API_URL}/v1/orders/report`,
    { headers }
  );
}



getOrderSummary(options?: {
  status?: string;
  search?: string;
  startDate?: Date;
  endDate?: Date;
}): Observable<any> {
  let headers = this.authHeaders(true);
  if (options?.status)    headers = headers.set('x-status',     options.status);
  if (options?.search)    headers = headers.set('x-search',     options.search);
  if (options?.startDate) headers = headers.set('x-start-date', options.startDate.toISOString());
  if (options?.endDate)   headers = headers.set('x-end-date',   options.endDate.toISOString());

  return this.http.get(
    `${this.API_URL}/v1/reports/orders/summary`,
    { headers }
  );
}

exportOrderReport(options?: {
  status?: string;
  search?: string;
  startDate?: Date;
  endDate?: Date;
}): Observable<Blob> {
  let headers = this.authHeaders(true);

  if (options?.status)    headers = headers.set('x-status',     options.status);
  if (options?.search)    headers = headers.set('x-search',     options.search);
  if (options?.startDate) headers = headers.set('x-start-date', options.startDate.toISOString());
  if (options?.endDate)   headers = headers.set('x-end-date',   options.endDate.toISOString());

  return this.http.get(
    `${this.API_URL}/v1/reports/orders/export`,
    { headers, responseType: 'blob' }
  );
}


    // Get Organization Status (Super Admin, Organization Admin)
    getOrganizationStatus(): Observable<any> {
      return this.http.get(
        `${this.API_URL}/organization/status`,
        { headers: this.authHeaders(true) }
      );
    }

  // Get logged-in user profile
      getMe(): Observable<any> {
        return this.http.get(
          `${this.API_URL}/user/me`,
          { headers: this.authHeaders(true) }
        );
      }

  // Get all organizations the logged-in user belongs to
  getUserOrganizations(): Observable<any> {
    return this.http.get(
      `${this.API_URL}/user/me/organizations`,
      { headers: this.authHeaders(true) }
    );
  }

  // Switch active organization — backend re-issues JWT with new organizationId
  switchOrganization(organizationId: string): Observable<any> {
    return this.http.post(
      `${this.API_URL}/user/me/switch-organization`,
      { organizationId },
      { headers: this.authHeaders(true) }
    );
  }

  // Build HTTP query parameters
  private buildParams(params: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value)); // Append valid params
      }
    });

    return httpParams;
  }


  searchAddress(name: string): Observable<any> {
    return this.http.post(
      `${this.API_URL}/organization/address/autocomplete`,
      { name },
      { headers: this.authHeaders(true) }
    );
  }

 reverseGeocode(lat: number, lng: number): Observable<any> {
  const headers = this.authHeaders(true);

  return this.http.get(
    `${this.API_URL}/organization/address/reverse-geocode`,
    {
      headers, params: {  lat: lat.toString(),  lng: lng.toString(), },
    }
  );
}


  updateOrganizationAddress(organizationId: string, address: any): Observable<any> {
    return this.http.put(
      `${this.API_URL}/organization/${organizationId}/address`,
      address,
      { headers: this.authHeaders(true) }
    );
  }

  // Check if store exists for logged-in user's organization
checkStoreExists(): Observable<{ success: boolean; hasStore: boolean }> {
  return this.http.get<{ success: boolean; hasStore: boolean }>(
    `${this.API_URL}/stores/exists`,
    { headers: this.authHeaders(true) }
  );
}

getUsers(page: number = 1, limit: number = 50): Observable<any> {
  return this.http.get<any>(
    `${this.API_URL}/users`,
    {
      headers: this.authHeaders(true),
      params: this.buildParams({ page, limit }),
    }
  );
}

createSuperAdmin(payload: { name: string; email: string; mobile: string; password: string }): Observable<any> {
  return this.http.post<any>(
    `${this.API_URL}/users`,
    payload,
    { headers: this.authHeaders(true) }
  );
}

createStoreUser(
  storeId: string,
  payload: { name: string; email: string; mobile: string; password: string }
): Observable<any> {
  return this.http.post<any>(
    `${this.API_URL}/store/${storeId}/users`,
    payload,
    { headers: this.authHeaders(true) }
  );
}

getRoles(): Observable<any> {
  return this.http.get<any>(
    `${this.API_URL}/roles/me`,
    { headers: this.authHeaders(true) }
  );
}

getUserById(id: string): Observable<any> {
  return this.http.get<any>(
    `${this.API_URL}/users/${id}`,
    { headers: this.authHeaders(true) }
  );
}

assignStore(userId: string, storeId: string): Observable<any> {
  return this.http.patch<any>(
    `${this.API_URL}/users/${userId}/assign-store`,
    { storeId },
    { headers: this.authHeaders(true) }
  );
}

updateUser(id: string, data: { enabled?: number; name?: string; mobile?: string; role?: string }): Observable<any> {
  return this.http.put<any>(
    `${this.API_URL}/users/${id}`,
    data,
    { headers: this.authHeaders(true) }
  );
}

toggleUserLock(id: string): Observable<any> {
  return this.http.patch<any>(
    `${this.API_URL}/users/${id}/lock`,
    {},
    { headers: this.authHeaders(true) }
  );
}

requestStoreAssignment(): Observable<any> {
  return this.http.patch<any>(
    `${this.API_URL}/user/me/request-store-assignment`,
    {},
    { headers: this.authHeaders(true) }
  );
}

getUserStores(userId: string): Observable<any> {
  return this.http.get<any>(
    `${this.API_URL}/users/${userId}/stores`,
    { headers: this.authHeaders(true) }
  );
}

addStoreAssignment(userId: string, storeId: string): Observable<any> {
  return this.http.post<any>(
    `${this.API_URL}/users/${userId}/stores`,
    { storeId },
    { headers: this.authHeaders(true) }
  );
}

removeStoreAssignment(userId: string, storeId: string): Observable<any> {
  return this.http.delete<any>(
    `${this.API_URL}/users/${userId}/stores/${storeId}`,
    { headers: this.authHeaders(true) }
  );
}

  // Create Order (POST /api/merchant/v1/orders)
  createOrder(payload: any): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/orders`,
      payload,
      { headers: this.authHeaders(true) }
    );
  }

  // Bulk Upload Orders (POST /api/merchant/v1/orders/bulk-upload)
    bulkUploadOrdersWithProgress(file: File): Observable<HttpEvent<any>> {
      const formData = new FormData();
      formData.append('file', file);
      const headers = this.authHeaders(true).delete('Content-Type');
      return this.http.post<HttpEvent<any>>(`${this.API_URL}/v1/orders/bulk-upload`,
        formData, {  headers, observe: 'events', reportProgress: true, }
      );
    }



  // Get Order By ID (GET /api/merchant/v1/orders/:orderId)
      getOrderById(orderId: string): Observable<any> {
        return this.http.get(
          `${this.API_URL}/v1/orders/${orderId}`,
          { headers: this.authHeaders(true) }
        );
      }

      getLspQuotes(orderId: string): Observable<any> {
        return this.http.get(
          `${this.API_URL}/v1/orders/${orderId}/lsp-quotes`,
          { headers: this.authHeaders(true) }
        );
      }

  getOrchestrationAnalytics(params: {
    page?: number;
    limit?: number;
    store?: string;
    orderId?: string;
    lsp?: string;
    city?: string;
    status?: string;
    manifested?: 'true' | 'false';
  }): Observable<any> {
    const q = new URLSearchParams();
    if (params.page)       q.set('page',       String(params.page));
    if (params.limit)      q.set('limit',       String(params.limit));
    if (params.store)      q.set('store',       params.store);
    if (params.orderId)    q.set('orderId',     params.orderId);
    if (params.lsp)        q.set('lsp',         params.lsp);
    if (params.city)       q.set('city',        params.city);
    if (params.status)     q.set('status',      params.status);
    if (params.manifested) q.set('manifested',  params.manifested);
    return this.http.get(
      `${this.API_URL}/v1/orders/orchestration?${q.toString()}`,
      { headers: this.authHeaders(true) }
    );
  }

  rebroadcastOrder(orderId: string): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/orders/${orderId}/rebroadcast`,
      {},
      { headers: this.authHeaders(true) }
    );
  }

  getOrchestrationDetail(orderId: string): Observable<any> {
    return this.http.get(
      `${this.API_URL}/v1/orders/orchestration/${orderId}`,
      { headers: this.authHeaders(true) }
    );
  }

  getOrchestrationOptions(): Observable<{ cities: { label: string; value: string }[]; statuses: string[] }> {
    return this.http.get<{ cities: { label: string; value: string }[]; statuses: string[] }>(
      `${this.API_URL}/v1/orders/orchestration/options`,
      { headers: this.authHeaders(true) }
    );
  }

  // Get Orders By Store (GET /api/merchant/v1/orders/store/:storeId)
  getOrdersByStore(storeId: string): Observable<any> {
    return this.http.get(
      `${this.API_URL}/v1/orders/store/${storeId}`,
      { headers: this.authHeaders(true) }
    );
  }

  // Upload Store Logo
  uploadStoreLogo(storeId: string, file: File ): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('file', file);
    const headers = this.authHeaders(true).delete('Content-Type');
    return this.http.post<HttpEvent<any>>( `${this.API_URL}/stores/${storeId}/logo`,
    formData, {   headers,  observe: 'events', reportProgress: true, });
  }


// Update logged-in user profile (name, mobile, logo)
updateUserProfile(payload: FormData): Observable<any> {
  const headers = this.authHeaders(true).delete('Content-Type');
  return this.http.post(
    `${this.API_URL}/user/me/update-profile`,
    payload,
    { headers }
  );
}

deleteAccount(): Observable<any> {
  return this.http.delete(`${this.API_URL}/user/me/account`, { headers: this.authHeaders() });
}


  // Dashboard Summary Counts
getDashboardSummary(): Observable<any> {
  return this.http.get(
    `${this.API_URL}/dashboard/summary`,
    { headers: this.authHeaders(true) }
  );
}

// Dashboard Fulfillment Stats
getDashboardFulfillmentStats(
  startDate: string,
  endDate: string
): Observable<any> {
  return this.http.get(
    `${this.API_URL}/dashboard/fulfillment-stats`,
    {
      headers: this.authHeaders(true),
      params: {
        startDate,
        endDate,
      },
    }
  );
}


  // ─── Complaint / IGM methods ────────────────────────────────────────────────

  createComplaint(payload: { data: { order_id: string; description: any } }): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/complaint/create`,
      payload,
      { headers: this.authHeaders(true) }
    );
  }

  updateComplaint(payload: { data: { _id: string; action: any } }): Observable<any> {
    return this.http.post(
      `${this.API_URL}/v1/complaint/update`,
      payload,
      { headers: this.authHeaders(true) }
    );
  }

  getMyTickets(options?: { page?: number; limit?: number; status?: string }): Observable<any> {
    let headers = this.authHeaders(true);
    if (options?.page)   headers = headers.set('x-page',   String(options.page));
    if (options?.limit)  headers = headers.set('x-limit',  String(options.limit));
    if (options?.status) headers = headers.set('x-status', options.status);
    return this.http.get(
      `${this.API_URL}/v1/complaint/my-tickets`,
      { headers }
    );
  }

  // ticketid is passed as a custom header per the backend contract.
  getTicketById(ticketId: string): Observable<any> {
    const headers = this.authHeaders(true).set('ticketid', ticketId);
    return this.http.get(
      `${this.API_URL}/v1/complaint/ticket-by-id`,
      { headers }
    );
  }

  getComplaintCategories(): Observable<any> {
    return this.http.get(
      `${this.API_URL}/v1/complaint/categories`,
      { headers: this.authHeaders(true) }
    );
  }

  getFallbackTickets(options?: { page?: number; limit?: number }): Observable<any> {
    let headers = this.authHeaders(true);
    if (options?.page)  headers = headers.set('x-page',  String(options.page));
    if (options?.limit) headers = headers.set('x-limit', String(options.limit));
    return this.http.get(
      `${this.API_URL}/v1/complaint/fallback-tickets`,
      { headers }
    );
  }

  createFallbackComplaint(formData: FormData): Observable<any> {
    const token = localStorage.getItem('auth_token');
    let headers = new HttpHeaders({
      subscriberid: environment.subscriberId,
      created_by:   environment.subscriberId,
    });
    if (token) {
      headers = headers.set('Authorization', token);
    }
    return this.http.post(
      `${this.API_URL}/v1/complaint/fallback`,
      formData,
      { headers }
    );
  }

  retryOrder(payload:any){
    let headers = this.authHeaders(true);
    return this.http.post(`${this.API_URL}/v1/orders/retry`,payload,{headers}) 
  }

}


